import React, { useCallback } from 'react';
import { Plus, Trash2, MessageSquare } from 'lucide-react';
import { useSlotScreenGeneratorStore } from '@store/slotScreenGeneratorStore';

const MODEL_LABELS: Record<string, string> = {
  'gemini-3.1-pro-preview': 'Pro',
  'gemini-3-flash-preview': 'Flash',
  'gemini-3.1-flash-image-preview': 'NB2',
};

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  return `${Math.floor(diff / 86_400_000)}d`;
}

export function IdeaChatSidebar() {
  const sessions = useSlotScreenGeneratorStore(s => s.ideaCanvas.sessions);
  const activeSessionId = useSlotScreenGeneratorStore(s => s.ideaCanvas.activeSessionId);
  const createSession = useSlotScreenGeneratorStore(s => s.createIdeaSession);
  const setActiveSession = useSlotScreenGeneratorStore(s => s.setActiveIdeaSession);
  const deleteSession = useSlotScreenGeneratorStore(s => s.deleteIdeaSession);

  const handleNew = useCallback(() => { createSession(); }, [createSession]);

  return (
    <div className="w-[200px] min-w-[200px] flex flex-col border-r border-zinc-800 bg-zinc-900/50">
      {/* New Chat button */}
      <div className="p-2 flex-shrink-0">
        <button
          onClick={handleNew}
          className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-zinc-200 bg-blue-600/80 hover:bg-blue-600 rounded-lg transition-colors"
        >
          <Plus size={14} />
          New Chat
        </button>
      </div>

      {/* Session list */}
      <div className="flex-1 overflow-y-auto px-1 pb-2">
        {sessions.length === 0 && (
          <div className="flex flex-col items-center justify-center h-32 text-zinc-600 text-xs gap-1">
            <MessageSquare size={20} strokeWidth={1} />
            <span>No chats yet</span>
          </div>
        )}
        {sessions.map(session => {
          const isActive = session.id === activeSessionId;
          return (
            <div
              key={session.id}
              onClick={() => setActiveSession(session.id)}
              className={`group flex flex-col gap-0.5 px-3 py-2 mx-1 mb-0.5 rounded-lg cursor-pointer transition-colors ${
                isActive ? 'bg-zinc-700' : 'hover:bg-zinc-800'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-200 truncate flex-1 leading-tight">
                  {session.title}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteSession(session.id); }}
                  className="p-0.5 text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  title="Delete chat"
                >
                  <Trash2 size={11} />
                </button>
              </div>
              <div className="flex items-center gap-1.5 text-[10px] text-zinc-500">
                <span className="px-1 py-0 bg-zinc-800 rounded text-zinc-400">
                  {MODEL_LABELS[session.modelId] ?? 'Flash'}
                </span>
                <span>{relativeTime(session.updatedAt)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
